import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import SystemHeader from '@/widgets/layout/SystemHeader'
import { useBackToParent, usePersistentPageSize } from '@/hooks'
import { queriesApi } from '@/services'
import { isHandledAuthError } from '@/services/client'
import { useToast } from '@/widgets/common'
import type { NotificationQueryRowResponse } from '@/services/queries/types'
import useSearchPersistence from '@/hooks/useSearchPersistence'
import { Pagination, BackButton, TableLoadingRow, DemoInput, DemoSelect, DemoBadge } from '@/widgets/demo/ListPrimitives';
import { demoButtonClass, computePagination } from '@/widgets/demo/listHelpers';
const NOTIFICATION_STATUS_OPTIONS = [
  { value: 'ALL', label: '通知状態（全て）' },
  { value: 'UNPRINTED', label: '未出力' },
  { value: 'PRINTED', label: '出力済' },
]

// 最後に適用した検索条件とページを localStorage に保持する（顧客要望：
// 「検索条件は最後に選択した結果を保持する」）。選択行などの一時状態は保持しない。
interface NotificationPersisted {
  identId: string
  name: string
  householdNo: string
  notificationStatus: string
  page: number
}

const DEFAULT_PERSISTED: NotificationPersisted = {
  identId: '',
  name: '',
  householdNo: '',
  notificationStatus: 'ALL',
  page: 1,
}

const NotificationOutput = () => {
  const goBackToParent = useBackToParent()
  const toast = useToast()

  const [savedSearch, setSavedSearch] = useSearchPersistence<NotificationPersisted>('notification-output', DEFAULT_PERSISTED)

  const [qIdentId, setQIdentId] = useState(savedSearch.identId)
  const [qName, setQName] = useState(savedSearch.name)
  const [qHouseholdNo, setQHouseholdNo] = useState(savedSearch.householdNo)
  const [qNotificationStatus, setQNotificationStatus] = useState(savedSearch.notificationStatus)
  const [applied, setApplied] = useState({
    identId: savedSearch.identId,
    name: savedSearch.name,
    householdNo: savedSearch.householdNo,
    notificationStatus: savedSearch.notificationStatus,
  })

  const [rows, setRows] = useState<NotificationQueryRowResponse[]>([])
  const [totalElements, setTotalElements] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [page, setPage] = useState(savedSearch.page ?? 1)
  const [pageSize, setPageSize] = usePersistentPageSize('notification-output')
  const [loading, setLoading] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState<Set<string>>(new Set())

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await queriesApi.notification({
        identId: applied.identId.trim() || undefined,
        name: applied.name.trim() || undefined,
        householdNo: applied.householdNo.trim() || undefined,
        notificationStatus: applied.notificationStatus === 'ALL' ? undefined : applied.notificationStatus,
        page: page - 1,
        size: pageSize,
      })
      // 取得ページが範囲外（永続化された page が絞り込み後の総ページ数を超える等）で
      // 0件返却された場合、該当データはあるのに「データなし」表示になる。最終ページへ寄せて再取得する。
      if (data.content.length === 0 && data.totalElements > 0 && page > 1) {
        setTotalElements(data.totalElements)
        setTotalPages(Math.max(1, data.totalPages || 1))
        setPage(Math.max(1, Math.ceil(data.totalElements / pageSize)))
        return
      }
      setRows(data.content)
      setTotalElements(data.totalElements)
      setTotalPages(Math.max(1, data.totalPages || 1))
    } catch (error) {
      setRows([])
      setTotalElements(0)
      setTotalPages(1)
      if (isHandledAuthError(error)) return
    } finally {
      setLoading(false)
    }
  }, [applied, page, pageSize])

  useEffect(() => {
    // 検索条件・ページ変更に応じて一覧を取得する正当なデータ取得 effect。
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchData()
  }, [fetchData])

  useEffect(() => {
    // 取得結果に存在しない選択行を取り除く同期 effect。
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedRowKeys((prev) => {
      const rowIds = new Set(rows.map((row) => String(row.applicationId)))
      const next = new Set<string>()
      prev.forEach((key) => {
        if (rowIds.has(key)) next.add(key)
      })
      return next
    })
  }, [rows])

  const runSearch = () => {
    setLoading(true)
    setPage(1)
    const nextApplied = {
      identId: qIdentId,
      name: qName,
      householdNo: qHouseholdNo,
      notificationStatus: qNotificationStatus,
    }
    setApplied(nextApplied)
    setSavedSearch({ ...nextApplied, page: 1 })
  }

  const runClear = () => {
    setLoading(true)
    setQIdentId('')
    setQName('')
    setQHouseholdNo('')
    setQNotificationStatus('ALL')
    setPage(1)
    const clearedApplied = {
      identId: '',
      name: '',
      householdNo: '',
      notificationStatus: 'ALL',
    }
    setApplied(clearedApplied)
    setSavedSearch({ ...clearedApplied, page: 1 })
  }

  const { safePage, rangeFrom, rangeTo } = computePagination(page, totalElements, pageSize)

  const pageRows = rows
  const allPageSelected = pageRows.length > 0 && pageRows.every((r) => selectedRowKeys.has(String(r.applicationId)))
  const somePageSelected = pageRows.some((r) => selectedRowKeys.has(String(r.applicationId)))
  const selectAllRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = somePageSelected && !allPageSelected
  }, [somePageSelected, allPageSelected])

  const toggleSelectAll = () => {
    const keys = pageRows.map((r) => String(r.applicationId))
    setSelectedRowKeys((prev) => {
      const next = new Set(prev)
      if (allPageSelected) keys.forEach((key) => next.delete(key))
      else keys.forEach((key) => next.add(key))
      return next
    })
  }

  const toggleRow = (key: string) => {
    setSelectedRowKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const handleBulkOutput = () => {
    toast.success(`${selectedRowKeys.size}件の通知書を出力しました（デモ表示）`)
  }

  const previewRow = useMemo(() => {
    if (selectedRowKeys.size > 0) {
      const hit = rows.find((row) => selectedRowKeys.has(String(row.applicationId)))
      if (hit) return hit
    }
    return rows[0] || null
  }, [rows, selectedRowKeys])

  return (
    <div className="min-h-screen bg-gray-100">
      <SystemHeader breadcrumbs={[{ label: '給付決定通知書出力' }]} />
      <div className="mx-auto max-w-7xl px-6 py-8">
        <BackButton onClick={goBackToParent} />
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-8">
            <div className="mb-6 rounded-xl bg-white p-6 shadow-lg">
              <h2 className="mb-4 text-xl font-bold text-gray-800">対象者検索</h2>
              <div className="mb-4 grid grid-cols-2 gap-4 md:grid-cols-5">
                <DemoInput type="text" value={qIdentId} onChange={(e) => setQIdentId(e.target.value)} placeholder="識別ID" />
                <DemoInput type="text" value={qName} onChange={(e) => setQName(e.target.value)} placeholder="氏名" />
                <DemoInput type="text" value={qHouseholdNo} onChange={(e) => setQHouseholdNo(e.target.value)} placeholder="世帯番号" />
                <DemoSelect value={qNotificationStatus} onChange={(e) => setQNotificationStatus(e.target.value)}>
                  {NOTIFICATION_STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </DemoSelect>
                <button type="button" onClick={runSearch} className={demoButtonClass.primary}>検索</button>
              </div>
              <div className="mb-4 flex justify-end">
                <button type="button" onClick={runClear} className={demoButtonClass.secondary}>クリア</button>
              </div>

              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="w-full">
                  <thead className="border-b border-gray-200 bg-gray-50">
                    <tr>
                      <th className="w-10 px-3 py-3 text-center">
                        <input
                          ref={selectAllRef}
                          type="checkbox"
                          checked={allPageSelected}
                          onChange={toggleSelectAll}
                          className="h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">識別ID</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">世帯番号</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">氏名</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">支給額</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">審査結果</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">通知状態</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">操作</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y divide-gray-200 text-sm transition-opacity ${loading ? 'opacity-60' : ''}`}>
                    {pageRows.length === 0 ? (
                      loading ? (
                        <TableLoadingRow colSpan={8} />
                      ) : (
                        <tr>
                          <td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">該当するデータがありません</td>
                        </tr>
                      )
                    ) : (
                      pageRows.map((row) => {
                        const key = String(row.applicationId)
                        return (
                          <tr key={key} className="transition hover:bg-blue-50">
                            <td className="w-10 px-3 py-3 text-center">
                              <input
                                type="checkbox"
                                checked={selectedRowKeys.has(key)}
                                onChange={() => toggleRow(key)}
                                className="h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                            </td>
                            <td className="px-4 py-3 font-mono text-xs">{row.identId}</td>
                            <td className="px-4 py-3 font-medium">{row.householdNo}</td>
                            <td className="px-4 py-3">{row.name}</td>
                            <td className="px-4 py-3 text-left">{row.benefitAmount != null ? row.benefitAmount.toLocaleString() : '－'}円</td>
                            <td className="px-4 py-3"><DemoBadge status={row.reviewStatusLabel} /></td>
                            <td className="px-4 py-3"><DemoBadge status={row.notificationStatusLabel} /></td>
                            <td className="px-4 py-3">
                              <button type="button" className={demoButtonClass.linkBlue}>プレビュー</button>
                            </td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination
                currentPage={safePage}
                totalPages={totalPages}
                total={totalElements}
                rangeFrom={rangeFrom}
                rangeTo={rangeTo}
                onPageChange={(next) => { setLoading(true); setPage(next); setSavedSearch({ ...applied, page: next }); }}
                pageSize={pageSize}
                onPageSizeChange={(next) => { setLoading(true); setPageSize(next); setPage(1); setSavedSearch({ ...applied, page: 1 }); }}
              />
              <div className="mt-4 flex items-center justify-between">
                <p className="text-sm text-gray-500">選択中: <span className="font-bold text-blue-600">{selectedRowKeys.size}件</span></p>
                <button
                  type="button"
                  onClick={handleBulkOutput}
                  disabled={selectedRowKeys.size === 0}
                  className={selectedRowKeys.size === 0 ? demoButtonClass.disabled : demoButtonClass.primarySm}
                >
                  選択データを一括出力
                </button>
              </div>
            </div>
          </div>

          <div className="col-span-4">
            <div className="sticky top-8 rounded-xl bg-white p-6 shadow-lg">
              <h2 className="mb-4 text-lg font-bold text-gray-800">通知書プレビュー</h2>
              <div className="min-h-80 rounded-lg border-2 border-gray-200 bg-gray-50 p-6">
                {!previewRow ? (
                  <div className="pt-12 text-center text-sm text-gray-500">プレビュー対象がありません</div>
                ) : (
                  <div className="space-y-3 text-sm">
                    <div className="text-center">
                      <p className="text-xs text-gray-500">令和8年4月1日</p>
                      <h3 className="mt-3 text-lg font-bold">給付決定通知書</h3>
                    </div>
                    <p>{previewRow.name} 様</p>
                    <div className="border-t border-gray-300 pt-3 text-gray-600">
                      下記のとおり給付を決定しましたので通知いたします。
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-white p-3">
                      <div className="flex justify-between"><span className="text-gray-600">世帯番号:</span><span className="font-medium">{previewRow.householdNo}</span></div>
                      <div className="mt-1 flex justify-between"><span className="text-gray-600">支給額:</span><span className="text-lg font-bold">{previewRow.benefitAmount != null ? previewRow.benefitAmount.toLocaleString() : '－'}円</span></div>
                      <div className="mt-1 flex justify-between"><span className="text-gray-600">状態:</span><span className="font-medium">{previewRow.notificationStatusLabel}</span></div>
                    </div>
                  </div>
                )}
              </div>
              <button type="button" className={`mt-4 w-full ${demoButtonClass.secondary}`}>PDF プレビュー</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default NotificationOutput
